/**
 * AuthGate sits between providers and the routed app. It listens to the
 * Amplify Authenticator state, shows a spinner while configuring, then
 * renders either the authenticated AppContent shell or the public auth routes.
 *
 * Once authenticated, AuthGate also drives the Redux user-role bootstrap
 * (`useInitUserRole`) before mounting `AppContent`. This ensures the route
 * tree never renders a frame where the user is authenticated but
 * `state.user.role` is still `null`, which previously produced a blank page
 * on first load.
 */
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { usePermissionsBootstrap } from '@/hooks/usePermissions';
import { logger } from '@/lib/logger';
import { PERMISSIONS_QUERY_KEY } from '@/lib/permissions-session';
import { signOutSession } from '@/lib/sign-out';
import { debug } from '@/utils/debug';
import FallbackSpinner from '../components/FallbackSpinner';
import { AppContent } from './AppContent';
import { useInitUserRole } from './hooks/useInitUserRole';
import { AuthRoutes } from './routes/AuthRoutes';

export const MAGIC_LINK_PATHS = ['/auth/magic-link', '/auth/magic-login'];

/**
 * Magic links (password reset, passwordless login) only exist in AuthRoutes.
 * Auth cookies are shared across tabs, so opening one while another tab is
 * signed in rendered the authenticated shell and its 404. Sign out first;
 * AuthGate then renders AuthRoutes at the same URL and the link completes.
 */
const SignOutForMagicLink = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  useEffect(() => {
    signOutSession({ dispatch, queryClient }).catch((error) =>
      logger.error('Sign-out before magic link failed', error)
    );
  }, [dispatch, queryClient]);

  return <FallbackSpinner />;
};

const PermissionsLoadFailure = () => {
  const queryClient = useQueryClient();

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6">
      <Alert variant="destructive" className="max-w-md">
        <AlertTitle>Your access could not be loaded.</AlertTitle>
        <AlertDescription>
          Try again. If this continues, sign out and sign back in.
        </AlertDescription>
      </Alert>
      <Button
        type="button"
        onClick={() => {
          queryClient.invalidateQueries({ queryKey: [...PERMISSIONS_QUERY_KEY] });
        }}
      >
        Try again
      </Button>
    </div>
  );
};

const AuthenticatedShell = () => {
  // Drive role/profile hydration above AppContent so the route tree is held
  // behind a spinner until Redux has a role to switch on.
  const { isInitializing: isRoleInitializing } = useInitUserRole();
  const { isLoading: isPermissionsLoading, isError: isPermissionsError } =
    usePermissionsBootstrap();

  if (isRoleInitializing || isPermissionsLoading) {
    debug.log('[AuthGate] Authenticated; auth bootstrap in flight - showing FallbackSpinner');
    return <FallbackSpinner />;
  }

  if (isPermissionsError) {
    return <PermissionsLoadFailure />;
  }

  return <AppContent />;
};

export const AuthGate = () => {
  const { authStatus, user } = useAuthenticator((context) => [context.authStatus, context.user]);
  const { pathname } = useLocation();
  const isMagicLinkPath = MAGIC_LINK_PATHS.includes(pathname);

  // Set once the magic link page has rendered signed-out, so the session the
  // link itself creates (after the new password is saved) is not signed out.
  const hasMagicLinkStartedRef = useRef(false);
  useEffect(() => {
    if (!isMagicLinkPath) hasMagicLinkStartedRef.current = false;
    else if (authStatus === 'unauthenticated') hasMagicLinkStartedRef.current = true;
  }, [authStatus, isMagicLinkPath]);

  debug.log('[AuthGate] Render', { authStatus, hasUser: !!user });

  if (authStatus === 'configuring') {
    debug.log('[AuthGate] Still configuring - showing FallbackSpinner');
    return <FallbackSpinner />;
  }

  if (authStatus === 'authenticated' && isMagicLinkPath && !hasMagicLinkStartedRef.current) {
    debug.log('[AuthGate] Magic link opened with an existing session - signing out first');
    return <SignOutForMagicLink />;
  }

  if (authStatus === 'authenticated' && user) {
    debug.log('[AuthGate] Authenticated with user - rendering AuthenticatedShell');
    return <AuthenticatedShell />;
  }

  if (authStatus === 'authenticated' && !user) {
    debug.log('[AuthGate] Authenticated but user not yet populated - showing FallbackSpinner');
    return <FallbackSpinner />;
  }

  debug.log('[AuthGate] Not authenticated - rendering AuthRoutes');
  return <AuthRoutes />;
};
